import { deepEqual } from './deepEqual';
import { isObject } from './isObject';

type ConfigObject = Record<string, unknown>;

/**
 * Extracts only the changed fields between original and updated config.
 * Returns an object with the structure of changed paths and their new values.
 *
 * @param original - The original config object
 * @param updated - The updated config object
 * @param includeDeletions - If true, deleted keys are represented as null in the result.
 *   Defaults to false for backward compatibility. Only CR flow should pass true.
 *
 * Example:
 *   original: { a: 1, b: { c: 2, d: 3 } }
 *   updated:  { a: 1, b: { c: 5, d: 3 } }
 *   result:   { b: { c: 5 } }
 *
 * Example with deletion (includeDeletions: true):
 *   original: { a: 1, b: 2 }
 *   updated:  { a: 1 }
 *   result:   { b: null }
 */
export function getChangedFields(original: ConfigObject, updated: ConfigObject, includeDeletions = false): ConfigObject {
  const changes: ConfigObject = {};

  // Check all keys in updated object
  Object.keys(updated).forEach((key) => {
    const originalValue = original[key];
    const updatedValue = updated[key];

    // If values are equal, skip
    if (deepEqual(originalValue, updatedValue)) {
      return;
    }

    // If both are plain objects, recurse to get nested changes (isObject
    // already excludes arrays).
    if (isObject(originalValue) && isObject(updatedValue)) {
      const nestedChanges = getChangedFields(originalValue as ConfigObject, updatedValue as ConfigObject, includeDeletions);
      if (Object.keys(nestedChanges).length > 0) {
        changes[key] = nestedChanges;
      }
    } else {
      // For primitives, arrays, or type changes - include the full new value
      changes[key] = updatedValue;
    }
  });

  // Check for deleted keys (exist in original but not in updated)
  // Only include deletions when explicitly requested (CR flow)
  if (includeDeletions) {
    Object.keys(original).forEach((key) => {
      if (!(key in updated)) {
        changes[key] = null;
      }
    });
  }

  return changes;
}

/**
 * Extracts the previous values for only the changed fields.
 * Returns an object with the same structure as changedFields but with original values.
 *
 * Example:
 *   original: { a: 1, b: { c: 2, d: 3 } }
 *   changedFields: { b: { c: 5 } }
 *   result:   { b: { c: 2 } }
 */
export function getPreviousValues(original: ConfigObject, changedFields: ConfigObject): ConfigObject {
  const previousValues: ConfigObject = {};

  Object.keys(changedFields).forEach((key) => {
    const changedValue = changedFields[key];
    const originalValue = original[key];

    // If the changed value is a plain object (nested changes), recurse.
    if (isObject(changedValue) && isObject(originalValue)) {
      const nestedPrevious = getPreviousValues(originalValue as ConfigObject, changedValue as ConfigObject);
      if (Object.keys(nestedPrevious).length > 0) {
        previousValues[key] = nestedPrevious;
      }
    } else {
      // For primitives or arrays - include the original value
      previousValues[key] = originalValue;
    }
  });

  return previousValues;
}
